import { AxiosInstance } from 'axios';
import { BaseRequest } from '@trukt/types/BaseRequest';
import { Charge } from '@trukt/portal-utils/Charge';
import { IntegrationApp } from '@trukt/types/Integration';

type OrderPartyChargeBaseRouteParams = { orderId: string; partyId: string };

type BaseCharge = Pick<Charge, 'amount' | 'itemId' | 'notes' | 'systemUsage' | 'dateCharged'>;

interface OrderPartyChargeAddRequestBody extends Omit<BaseCharge, 'dateCharged'> {}

export type OrderPartyChargeAddRequest = Pick<
  BaseRequest<void, OrderPartyChargeAddRequestBody, OrderPartyChargeBaseRouteParams>,
  'body' | 'routeParams'
>;

type OrderPartyChargeAddResponse = {
  guid: string;
};

type OrderPartyChargeSearchRequest = OrderPartyChargeBaseRouteParams;
type OrderPartyChargeSearchResponse = Charge[];

interface OrderPartyChargeUpdateRouteParams extends OrderPartyChargeBaseRouteParams {
  chargeId: string;
}

interface OrderPartyChargeUpdateBody extends Partial<BaseCharge> {
  integrations?: { appName: IntegrationApp['name']; externalId: string | null }[];
}

export type OrderPartyChargeUpdateRequest = Pick<
  BaseRequest<void, OrderPartyChargeUpdateBody, OrderPartyChargeUpdateRouteParams>,
  'routeParams' | 'body'
>;

type OrderPartyChargeUpdateResponse = { id: string };

export type OrderPartyChargeDeleteRequest = OrderPartyChargeUpdateRouteParams;
type OrderPartyChargeDeleteResponse = void;

type OrderPartyChargeExportBody = { lines: string[] };
export type OrderPartyChargeExportRequest = BaseRequest<
  void,
  OrderPartyChargeExportBody,
  OrderPartyChargeBaseRouteParams
>;

export type OrderPartyChargeReleaseRequest = BaseRequest<
  void,
  OrderPartyChargeExportBody,
  OrderPartyChargeBaseRouteParams
>;

export class OrderPartyChargeService {
  instance: AxiosInstance;
  constructor(instance: AxiosInstance) {
    this.instance = instance;
  }

  add(request: OrderPartyChargeAddRequest) {
    return this.instance.post<OrderPartyChargeAddResponse>(
      `/orders/${request.routeParams.orderId}/parties/${request.routeParams.partyId}/charges`,
      request.body,
    );
  }

  search(request: OrderPartyChargeSearchRequest) {
    return this.instance.get<OrderPartyChargeSearchResponse>(
      `/orders/${request.orderId}/parties/${request.partyId}/charges`,
    );
  }

  update(request: OrderPartyChargeUpdateRequest) {
    return this.instance.patch<OrderPartyChargeUpdateResponse>(
      `/orders/${request.routeParams.orderId}/parties/${request.routeParams.partyId}/charges/${request.routeParams.chargeId}`,
      request.body,
    );
  }

  delete(request: OrderPartyChargeDeleteRequest) {
    return this.instance.delete<OrderPartyChargeDeleteResponse>(
      `/orders/${request.orderId}/parties/${request.partyId}/charges/${request.chargeId}`,
    );
  }

  export(request: OrderPartyChargeExportRequest) {
    return this.instance.put(
      `/orders/${request.routeParams.orderId}/parties/${request.routeParams.partyId}/charges/export`,
      request.body,
    );
  }

  release(request: OrderPartyChargeReleaseRequest) {
    return this.instance.put(
      `/orders/${request.routeParams.orderId}/parties/${request.routeParams.partyId}/charges/payment-release`,
      request.body,
    );
  }
}
